/**
 * WhatsApp's recipes as data (Mac recipes R3), from the research (comms/whatsapp.md). Each passes the
 * program lint and ships held until the WhatsApp drill lifts it. WhatsApp 26 is Mac Catalyst with no
 * AppleScript, and its menus are built in code: the research could read their titles but not the top
 * menus they sit in, and a menu press needs its top menu. So most of its recipes wait on the R3 review
 * lists for that harvest; what is here needs no menu.
 */
import { opening, recipeApp, type OsRecipe } from '../os-recipes.js';

const WHATSAPP = recipeApp('net.whatsapp.WhatsApp');

/** A reversible chord in the running app. */
const chord = (id: string, title: string, description: string, key: string, cues: readonly string[], reply: string): OsRecipe => ({
  id, app: WHATSAPP, status: 'held', title, description, params: {}, steps: [{ op: 'activate' }, { op: 'key', chord: key }], tier: 'reversible', cues, reply,
});

export const RECIPES: readonly OsRecipe[] = [
  // drill: that cmd+f focuses the chat-list search in 26.x, and its AX label (labelIn), before this is lifted
  {
    id: 'whatsapp.search', app: WHATSAPP, status: 'held', opens: true,
    title: 'Search WhatsApp chats',
    description: 'Filters the chat list as the words are typed; nothing is pressed after them. For asks like "search whatsapp for the flat address" or "find the chat with the plumber".',
    params: {
      query: { kind: 'literal', description: 'The exact words to search for', cut: 'query' },
    },
    steps: [
      { op: 'activate' },
      { op: 'focus_mark' },
      { op: 'key', chord: 'cmd+f' },
      { op: 'focus_moved', timeoutMs: 800, labelIn: ['search'] },
      { op: 'type', param: 'query' },
    ],
    tier: 'reversible', cues: ['search', 'find', 'look', 'look up the group'],
    reply: 'Searched WhatsApp for {query}.',
  },
  opening(chord('whatsapp.settings', 'Open WhatsApp settings', 'For asks like "open whatsapp settings" or "whatsapp preferences".', 'cmd+,', ['settings', 'preferences', 'prefs', 'setting', 'notification settings', 'change whatsapp settings'], 'Opened WhatsApp settings.')),
  // A named person is whatsapp.draft instead, which verifies the contact.
  {
    id: 'whatsapp.share_text', app: WHATSAPP, status: 'held',
    title: 'Share text to WhatsApp, choosing the chat there',
    description: 'Opens WhatsApp\'s own chat picker with their words; they pick the chat and send it themselves. For asks like "share this link on whatsapp: https://…" or "send this to someone on whatsapp: meet at 5".',
    params: {
      text: { kind: 'literal', description: 'The exact words or link to share', cut: 'after_saying' },
    },
    steps: [{ op: 'open_url', template: 'whatsapp://send?text={text}' }, { op: 'await_front', timeoutMs: 3000 }],
    tier: 'reversible', cues: ['share', 'whatsapp', 'share this link'],
    reply: 'WhatsApp is open with it. Pick the chat to send it to.',
  },
  {
    id: 'whatsapp.full_screen_on', app: WHATSAPP, status: 'held',
    title: 'Put WhatsApp in full screen',
    description: 'Acts on the front WhatsApp window (the call window during a call). For asks like "make whatsapp full screen" or "full screen whatsapp".',
    params: {},
    steps: [{ op: 'activate' }, { op: 'verb', id: 'sysevents.window_fullscreen_set', params: { enabled: true } }],
    tier: 'reversible', cues: ['full', 'fullscreen', 'maximize', 'full screen'],
    reply: 'Put WhatsApp in full screen.',
  },
];
